/**
 * What a counter leaves behind when it fails
 * (docs/plans/handheld-fleet.html 13).
 *
 * Two documents travel from a device to the platform and both end up as an
 * OBJECT in the FLEET bucket, referenced from an audit row by its KEY: a crash
 * report, posted on the launch after the crash, and a diagnostic bundle, posted
 * because an operator asked for one (`UPLOAD_DIAGNOSTICS`). Neither is ever a
 * row, and neither is ever in MEDIA - `GET /media/*` is unauthenticated and
 * serves every prefix outside the signed ones, so a bundle there would be a
 * public download of a counter's log (plan 18).
 *
 * ## Nothing in here is personal, and the caps are what keep it that way
 *
 * `stack` is a Java stack trace and `lastLines` are the device's own log lines,
 * which are written by the code that knows which fields hold a name - an order
 * id is fine, a guest's name is not (plan 13). The platform cannot enforce that
 * from here; what it CAN enforce is size, so the caps below are the contract's
 * half of the bargain: 50 lines of at most 300 characters, one stack of at most
 * 16 000, which is the plan's 64 KB with room to spare. A device that wants to
 * send more is a device that has started sending something else.
 *
 * ## Why the bundle carries a command id
 *
 * The upload IS the acknowledgement. An operator asks for diagnostics, the
 * device collects the command on its next beat and answers with the bundle -
 * one call rather than two, so a counter on a bakery's Wi-Fi cannot upload the
 * evidence and then fail to say that it did, which would leave the command
 * sitting Delivered for ever on the operator's page.
 */

import { z } from 'zod';
import { Instant, Uuid } from './common';
import { DeviceCommandOutcome } from './handheld-commands';
import { HandheldConfig } from './handheld-config';
import { HandheldInstallOutcome, HandheldTelemetry } from './handheld-telemetry';

/** At most this many crash reports are STORED per device per service day. */
export const CRASH_REPORTS_PER_DAY = 10;

/** The log ring the device keeps, capped on the wire as it is on the device. */
export const DIAGNOSTIC_RING_MAX_CHARS = 262_144;

/**
 * One uncaught exception, as the device recorded it and posts it on the next
 * launch (plan 13).
 *
 * `occurredAt` is the DEVICE's instant and is used as the object's name, which
 * is deliberate: two crashes a second apart are two objects, and a crash the
 * device could not post until the network came back keeps the time it happened
 * rather than the time it was told about.
 */
export const HandheldCrashReport = z.object({
  occurredAt: Instant,
  appVersionCode: z.int().positive().max(2_100_000_000),
  /** The stack trace, verbatim. */
  stack: z.string().min(1).max(16_000),
  /** `SystemClock.elapsedRealtime()` at the crash: how long it had been up. */
  uptimeSeconds: z.int().nonnegative(),
  /** The tail of the log ring - what the app was doing (plan 13). */
  lastLines: z.array(z.string().max(300)).max(50),
});
export type HandheldCrashReport = z.infer<typeof HandheldCrashReport>;

/**
 * The platform's answer to a crash report.
 *
 * The KEY it was stored under is deliberately NOT here: the device has no use
 * for it, and an address it could repeat back is an address it could be made to
 * ask for. The audit row carries the key, and Internal is where it is read.
 */
export const HandheldCrashAccepted = z.object({ recordedAt: Instant });
export type HandheldCrashAccepted = z.infer<typeof HandheldCrashAccepted>;

/**
 * Everything a counter can say about itself at once, on demand (plan 13).
 *
 * The ring, the last crash, the current telemetry and the device's own view of
 * its configuration - so an operator reading the object can tell what the
 * device THINKS it is configured with from what the platform resolved for it,
 * which is the one comparison a support call turns on.
 */
export const HandheldDiagnosticsBundle = z.object({
  /** The `UPLOAD_DIAGNOSTICS` command this answers; the upload acknowledges it. */
  commandId: Uuid,
  ring: z.string().max(DIAGNOSTIC_RING_MAX_CHARS),
  lastCrash: HandheldCrashReport.nullable(),
  telemetry: HandheldTelemetry,
  /**
   * What the DEVICE thinks it is configured with - and the two administered
   * thresholds on it are optional HERE while they are required on the beat that
   * carries them out.
   *
   * The asymmetry is the point: this half is uploaded BY a counter, and a build
   * older than 2026-08-30 echoes back the four settings it knows and neither
   * threshold. Refusing that upload would lose the one document a support call
   * about that very build turns on.
   */
  config: HandheldConfig.partial({ batteryLowPercent: true, storageLowMb: true }),
});
export type HandheldDiagnosticsBundle = z.infer<typeof HandheldDiagnosticsBundle>;

/**
 * The bundle's answer, which is also the command's.
 *
 * `outcome` is the FIRST outcome the command record holds, exactly as
 * `ackDeviceCommand` answers - a bundle posted twice reports the first ack, not
 * the second.
 *
 * It is NULL when nothing was acknowledged, and that is not an error: the
 * platform no longer holds a command under the id the device named (pruned or
 * expired away), or the command it does hold is not an `UPLOAD_DIAGNOSTICS`, so
 * marking it done would record that a tone rang on the evidence of a file. The
 * document is stored and audited either way - it is already in the counter's
 * hand and this request is the only chance to take it.
 */
export const HandheldDiagnosticsAccepted = z.object({
  recordedAt: Instant,
  outcome: DeviceCommandOutcome.nullable(),
});
export type HandheldDiagnosticsAccepted = z.infer<typeof HandheldDiagnosticsAccepted>;

/**
 * The last segment of a diagnostic object's key, and the ONLY part of it that
 * ever travels in a path.
 *
 * A strict pattern rather than a string, because this is the parameter a
 * traversal would arrive in: the worker composes the full key as
 * `diagnostics/<device row id>/<name>`, so a name that cannot contain a slash
 * or a dot pair cannot address another device's object - or anything else in
 * the bucket. The shape is the one `handheld-diagnostics.ts` writes: an ISO
 * instant with its colons removed, which is what makes it legal in a key and
 * still sortable.
 */
export const DiagnosticObjectName = z
  .string()
  .regex(/^(crash|bundle)-\d{4}-\d{2}-\d{2}T\d{6}\.\d{3}Z\.json$/);
export type DiagnosticObjectName = z.infer<typeof DiagnosticObjectName>;

/** What kind of document one object holds; read from the name, never stored. */
export const DiagnosticObjectKind = z.enum(['CRASH', 'BUNDLE']);
export type DiagnosticObjectKind = z.infer<typeof DiagnosticObjectKind>;

/**
 * One object a device has left behind, as the operator's page lists it.
 *
 * `key` is the whole FLEET key and is shown nowhere - it is what an incident
 * note quotes, and what the audit row for the upload carries. `name` is what
 * the download link addresses.
 */
export const DiagnosticObject = z.object({
  key: z.string().min(1).max(200),
  name: DiagnosticObjectName,
  kind: DiagnosticObjectKind,
  sizeBytes: z.int().nonnegative(),
  /** When the bucket accepted it, which for a crash is not when it happened. */
  uploadedAt: Instant,
});
export type DiagnosticObject = z.infer<typeof DiagnosticObject>;

/**
 * How far a build has actually got (plan 12).
 *
 * Counted from the device rows' own telemetry - the version code the counter
 * reported, never a number the platform assumed from what it offered. A device
 * on a build old enough to predate telemetry has no code at all and is counted
 * under `versionCode: null`, labelled with the version STRING it does report,
 * because "seven counters on something we cannot name" is a fact an operator
 * needs during a rollout and a bucket that silently dropped them is not.
 */
export const HandheldAdoptionRow = z.object({
  versionCode: z.int().positive().max(2_100_000_000).nullable(),
  /** The build's name where the platform holds it, or what the device reports. */
  versionName: z.string().max(40).nullable(),
  devices: z.int().nonnegative(),
  /**
   * The counters in this bucket whose last install attempt FAILED, by name.
   *
   * Named rather than counted, because the next move is a phone call to one
   * shop: a rollout is judged on which counters are stuck, not on how many.
   */
  failed: z
    .array(
      z.object({
        deviceId: Uuid,
        label: z.string().min(1).max(60),
        /** Null for a handheld on the fleet and paired to no shop. */
        locationName: z.string().min(1).max(200).nullable(),
        outcome: HandheldInstallOutcome,
      }),
    )
    .max(500),
});
export type HandheldAdoptionRow = z.infer<typeof HandheldAdoptionRow>;

export const HandheldAdoption = z.object({
  /** Newest build first; the un-numbered bucket last. */
  versions: z.array(HandheldAdoptionRow).max(200),
  asOf: Instant,
});
export type HandheldAdoption = z.infer<typeof HandheldAdoption>;

/**
 * Why a rollout stopped (plan 12).
 *
 * Three reasons and they are not interchangeable: an operator looked and
 * stopped it, or the platform stopped it itself on crashes, or on installs that
 * never completed. The last two are what "a bad build stops itself at ten
 * counters at 02:00 without anybody watching a dashboard" means, and the alert
 * says which so nobody has to reconstruct it from timestamps.
 */
export const HandheldHaltReason = z.enum(['OPERATOR', 'CRASH_LOOP', 'INSTALL_FAILURES']);
export type HandheldHaltReason = z.infer<typeof HandheldHaltReason>;
